import { Form } from 'formik';
import { FormikDebounce } from '../../../common/form/FormikDebounce';
import { FC } from 'react';
import * as Yup from 'yup';
import { validations } from '../../../../utils/validations';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { FormContainer } from '../../../common/form/FormContainer';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { SectionHeader } from '../../../common/SectionHeader';
import { getLangNameObj, getWorkplaceNameObj } from '../../../../utils/format';
import { QcsAutocomplete } from '../../../common/form/QcsAutocomplete';
import { useInvitation } from './invitationFunctions';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { InvitationState } from '@api/models/enums/InvitationState';
import { invitationPersonService } from '@api/services/services';
import { ShowError } from '../../../ShowError';
import { resources } from '@api/resources';

interface BulkEditForm {
  activities: ReferenceDto[];
  workplaces: ReferenceDto[];
}

interface Props {
  persons: InvitedPersonDto[];
  onClose: (saved?: boolean) => void;
}

export const InvitedPersonsBulkEditModal: FC<Props> = ({ persons, onClose }) => {
  const { t, i18n, invitation, callWithLock, reloadInvitation } = useInvitation();

  const handleSubmit = async (formData: BulkEditForm) => {
    await callWithLock(async () => {
      await invitationPersonService.bulkUpdatePersons(invitation.id!, {
        personIds: persons.map((p) => p.id!),
        activities: formData.activities.map((x) => x.id),
        workplaces: formData.workplaces.map((x) => x.id),
      });
      await reloadInvitation();
      onClose(true);
    });
  };

  return (
    <QcsModal open onClose={() => onClose()}>
      <QcsModalBox>
        <SectionHeader title="invitation.persons.bulkEdit" />
        <FormikDebounce<BulkEditForm>
          key={persons.map((p) => p.id).join(',')}
          initialValues={{
            activities: [],
            workplaces: [],
          }}
          validationSchema={Yup.object().shape({
            activities: validations.arrayNotEmpty(t),
            workplaces: validations.arrayNotEmpty(t),
          })}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <QcsAutocomplete
                name="activities"
                label={t('invitation.persons.activities')}
                required={true}
                multiple={true}
                disabled={invitation.state === InvitationState.Archived}
                options={invitation.activities?.map((x) => ({
                  id: x.activity?.id,
                  name: x.activity?.name,
                  nameEn: x.activity?.nameEn,
                }))}
                getOptionId={(option) => option.id!}
                getOptionName={(option) => getLangNameObj(i18n, option)}
                loading={false}
                loadingError={false}
              />
              <QcsAutocomplete
                name="workplaces"
                label={t('invitation.persons.workplaces')}
                required={true}
                multiple={true}
                disabled={invitation.state === InvitationState.Archived}
                options={invitation.workplaces?.map((x) => ({
                  id: x.workplace?.id,
                  name: getWorkplaceNameObj(i18n, x.workplace, x.establishment),
                }))}
                getOptionId={(option) => option.id!}
                getOptionName={(option) => getLangNameObj(i18n, option)}
                loading={false}
                loadingError={false}
              />

              <ShowError resource={resources.bulkUpdatePersons} />
              <SubmitButton disabled={invitation.state === InvitationState.Archived} />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
